import React, { useState, useEffect } from "react";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Checkbox,
	FormHelperText,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
	deleteSession,
	getSessionById,
	postSession,
	updateSession,
	reloadSessionContent,
} from "../../../../api/session";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";

// Tema para el botón de borrar (gris corporativo #4D4F53; la paleta no tiene rojo)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#4D4F53",
		},
	},
});

function getEmptySession() {
	return {
		name: "",
		code: "",
		numTables: 5,
		demo: false,
		settings: {
			maxChars: 500,
			participantsRegistration: false,
		},
	};
}

function stringifyContent(content) {
	if (!content) return "";
	try {
		return JSON.stringify(content, null, 2);
	} catch (e) {
		return "";
	}
}

export default function EditSessionPage() {
	const [session, setSession] = useState(getEmptySession());
	const [contentText, setContentText] = useState("");
	const [contentDirty, setContentDirty] = useState(false);
	const [contentError, setContentError] = useState(null);
	const [contentVersion, setContentVersion] = useState(null);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [openReloadDialog, setOpenReloadDialog] = useState(false);
	const sessionId = useParams().id;
	const history = useHistory();

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!sessionId) {
			disableLoadingData();
			return;
		}
		getSessionById(sessionId)
			.then((res) => {
				if (res.status === 200) {
					const data = res.data;
					setSession({
						name: data.name || "",
						code: data.code || "",
						numTables: data.numTables || 5,
						demo: !!data.demo,
						settings: {
							...(data.settings || {}),
							maxChars: data.settings?.maxChars ?? 500,
							participantsRegistration: !!data.settings
								?.participantsRegistration,
						},
					});
					setContentText(stringifyContent(data.content));
					setContentVersion(data.contentVersion || null);
					setContentDirty(false);
					setContentError(null);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "No se ha podido cargar la sesión.",
				});
				history.push("/sessions");
			});
	}, [sessionId, disableLoadingData, history]);

	function validateContent() {
		if (!contentText.trim()) return { ok: true, content: undefined };
		try {
			const parsed = JSON.parse(contentText);
			if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
				return { ok: false, error: "El contenido debe ser un objeto JSON." };
			setContentError(null);
			return { ok: true, content: parsed };
		} catch (e) {
			const msg = "JSON no válido: " + e.message;
			setContentError(msg);
			return { ok: false, error: msg };
		}
	}

	function buildBody() {
		const numTables = parseInt(session.numTables, 10);
		if (!session.name.trim() || !session.code.trim()) {
			alertError({
				error: null,
				customMessage: "Nombre y código son obligatorios.",
			});
			return null;
		}
		if (Number.isNaN(numTables) || numTables < 1 || numTables > 7) {
			alertError({
				error: null,
				customMessage: "El número de mesas debe estar entre 1 y 7.",
			});
			return null;
		}
		const maxChars = parseInt(session.settings.maxChars, 10);
		const body = {
			name: session.name.trim(),
			code: session.code.trim(),
			numTables,
			demo: !!session.demo,
			settings: {
				...session.settings,
				maxChars: Number.isNaN(maxChars) ? 500 : maxChars,
				participantsRegistration: !!session.settings
					.participantsRegistration,
			},
		};
		if (contentDirty) {
			const check = validateContent();
			if (!check.ok) {
				alertError({ error: null, customMessage: check.error });
				return null;
			}
			if (check.content) body.content = check.content;
		}
		return body;
	}

	function saveSession() {
		const body = buildBody();
		if (!body) return;
		if (!sessionId) {
			postSession(body)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "Guardada",
							customMessage: "Sesión creada correctamente.",
						});
						history.push("/sessions");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "No se ha podido crear la sesión.",
					});
				});
		} else {
			updateSession(sessionId, body)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: "Guardada",
							customMessage: "Cambios guardados correctamente.",
						});
						history.push("/sessions");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "No se han podido guardar los cambios.",
					});
				});
		}
	}

	function handleReloadContent() {
		reloadSessionContent(sessionId)
			.then((res) => {
				if (res.status === 200) {
					alertSuccess({
						title: "Contenido recargado",
						customMessage:
							res.data?.message ||
							"Contenido recargado desde la semilla.",
					});
					// Volvemos a cargar la sesión para mostrar el contenido nuevo
					return getSessionById(sessionId);
				}
				return null;
			})
			.then((res) => {
				if (res && res.status === 200) {
					setContentText(stringifyContent(res.data.content));
					setContentVersion(res.data.contentVersion || null);
					setContentDirty(false);
					setContentError(null);
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "No se ha podido recargar el contenido.",
				});
			});
	}

	const handleChange = (element) => (event) => {
		setSession({ ...session, [element]: event.target.value });
	};

	const handleChangeCheck = (element) => (event) => {
		setSession({ ...session, [element]: event.target.checked });
	};

	const handleChangeSetting = (element, isCheck = false) => (event) => {
		const value = isCheck ? event.target.checked : event.target.value;
		setSession({
			...session,
			settings: { ...session.settings, [element]: value },
		});
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader
						title={sessionId ? "Editar sesión" : "Nueva sesión"}
					></CardHeader>
					<CardBody>
						<TextField
							id={`name`}
							label="Nombre"
							value={session.name}
							onChange={handleChange("name")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
							fullWidth
						/>
						<TextField
							id={`code`}
							label="Código"
							value={session.code}
							onChange={handleChange("code")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							helperText="Identificador corto y único (p. ej. daiichi-2026)."
							required
						/>
						<br />
						<TextField
							id={`numTables`}
							label="Nº de mesas"
							type="number"
							inputProps={{ min: 1, max: 7 }}
							value={session.numTables}
							onChange={handleChange("numTables")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							helperText="Entre 1 y 7."
							required
						/>
						<TextField
							id={`maxChars`}
							label="Máx. caracteres por respuesta de mesa"
							type="number"
							inputProps={{ min: 20, max: 1000 }}
							value={session.settings.maxChars}
							onChange={handleChangeSetting("maxChars")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							style={{ marginLeft: "20px" }}
						/>
						<br />
						<FormControlLabel
							control={
								<Checkbox
									checked={!!session.demo}
									onChange={handleChangeCheck("demo")}
									name="demo"
									color="primary"
								/>
							}
							label="Sesión de demo / ensayo"
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={
										!!session.settings
											.participantsRegistration
									}
									onChange={handleChangeSetting(
										"participantsRegistration",
										true
									)}
									name="participantsRegistration"
									color="primary"
								/>
							}
							label="Las mesas registran a sus participantes"
						/>
					</CardBody>
				</Card>

				<Card>
					<CardHeader
						title={
							"Contenido (JSON)" +
							(contentVersion ? ` · versión ${contentVersion}` : "")
						}
					>
						{sessionId && (
							<CardHeaderToolbar>
								<button
									type="button"
									className="btn btn-primary"
									onClick={() => setOpenReloadDialog(true)}
								>
									Recargar contenido desde semilla
								</button>
							</CardHeaderToolbar>
						)}
					</CardHeader>
					<CardBody>
						<FormHelperText>
							{sessionId
								? "Edita el contenido de la dinámica (títulos, ejes, datos clave, pregunta de inicio, encuesta…). Solo se guarda si lo has modificado y es JSON válido."
								: "Opcional. Si se deja vacío, la sesión se crea con el contenido de la semilla del backend."}
						</FormHelperText>
						<textarea
							className="form-control mt-3"
							style={{
								fontFamily: "monospace",
								fontSize: "12px",
								minHeight: "480px",
								whiteSpace: "pre",
							}}
							spellCheck={false}
							value={contentText}
							onChange={(e) => {
								setContentText(e.target.value);
								setContentDirty(true);
								setContentError(null);
							}}
							onBlur={() => {
								if (contentDirty) validateContent();
							}}
						/>
						{contentError && (
							<FormHelperText error>{contentError}</FormHelperText>
						)}
						{contentDirty && !contentError && (
							<FormHelperText>
								Contenido modificado: se enviará al guardar.
							</FormHelperText>
						)}
					</CardBody>
				</Card>

				<Button
					onClick={() => history.push("/sessions")}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Volver
				</Button>
				<Button
					onClick={() => saveSession()}
					variant="outlined"
					color="primary"
					style={{ marginRight: "20px" }}
				>
					Guardar sesión
				</Button>
				{sessionId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Eliminar sesión
							</Button>
						</MuiThemeProvider>

						<ConfirmDialog
							title={"¿Seguro que quieres eliminar esta sesión?"}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteSession(sessionId)
									.then((res) => {
										if (
											res.status === 204 ||
											res.status === 200
										) {
											alertSuccess({
												title: "Eliminada",
												customMessage:
													"Sesión eliminada correctamente.",
											});
											history.push("/sessions");
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"No se ha podido eliminar la sesión.",
										});
									});
							}}
						>
							{
								"Se borrarán también sus mesas, respuestas, expectativas y respuestas de encuesta."
							}
						</ConfirmDialog>
						<ConfirmDialog
							title={"¿Recargar el contenido desde la semilla?"}
							open={openReloadDialog}
							setOpen={setOpenReloadDialog}
							onConfirm={handleReloadContent}
						>
							{
								"Se sustituirá el contenido actual de la sesión por el fichero de semilla del backend. Los cambios no guardados del editor se perderán."
							}
						</ConfirmDialog>
					</>
				)}
			</>
		);
}
